import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {Icon} from '@iconify/react';
import {createPortal} from 'react-dom';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import usePresScale from '../../../../../util/usePresScale';
import BottomSheet from '../../../../../ui/components/common/BottomSheet';

interface AnswerCommentProps {
    comment: string;
    showComment: boolean;
    setShowComment?: (show: boolean) => void;
    anchorRef: React.RefObject<HTMLDivElement | null>;
    device?: DeviceType;
    answer?: string;
}

interface PortalTarget {
    root: HTMLElement;
    isScaledContainer: boolean;
}

function getPortalTarget(anchorEl: HTMLElement | null): PortalTarget & {inFrame: boolean} {
    const ownerDocument = anchorEl?.ownerDocument;
    if (ownerDocument && ownerDocument !== document && ownerDocument.body) {
        return {root: ownerDocument.body, isScaledContainer: false, inFrame: true};
    }

    const canvasHolder = anchorEl?.closest('#canvas-holder');
    return canvasHolder instanceof HTMLElement
        ? {root: canvasHolder, isScaledContainer: true, inFrame: false}
        : {root: document.body, isScaledContainer: false, inFrame: false};
}

function calcDropdownPlacement(
    anchorRect: DOMRect,
    portalRect: DOMRect,
    dropdownHeight: number,
    scale: number
): {shouldOpenTop: boolean; maxHeight: number} {
    const spaceAbove = (anchorRect.top - portalRect.top) / scale;
    const spaceBelow = (portalRect.bottom - anchorRect.bottom) / scale;
    const shouldOpenTop = dropdownHeight > spaceBelow && spaceAbove > spaceBelow;
    const availableSpace = shouldOpenTop ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(availableSpace - 8, 80);

    return {shouldOpenTop, maxHeight};
}

function buildDropdownStyle(
    anchorRect: DOMRect,
    portalRect: DOMRect,
    shouldOpenTop: boolean,
    maxHeight: number,
    scale: number
): React.CSSProperties {
    return {
        position: 'absolute',
        top: shouldOpenTop ? 'auto' : (anchorRect.bottom - portalRect.top) / scale - 3,
        bottom: shouldOpenTop ? (portalRect.bottom - anchorRect.top) / scale - 3 : 'auto',
        left: (anchorRect.left - portalRect.left) / scale,
        width: anchorRect.width / scale,
        maxHeight,
    };
}

function AnswerComment({comment, showComment, setShowComment, anchorRef, device, answer}: AnswerCommentProps): ReactElement {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const [dropdownPlacement, setDropdownPlacement] = useState<'top' | 'bottom'>('bottom');
    const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(() => (typeof document !== 'undefined' ? document.body : null));
    const [isScaledContainer, setIsScaledContainer] = useState(false);
    const [inFrame, setInFrame] = useState(false);
    const [hoverOpened, setHoverOpened] = useState(false);
    const isMobile = device === DeviceType.MOBILE;
    const {scale: canvasScale} = usePresScale();
    // eslint-disable-next-line no-nested-ternary
    const effectiveScale = inFrame ? 1 : isScaledContainer ? canvasScale || 1 : 1;
    const sizeCompensation = inFrame || isMobile || isScaledContainer ? 1 : canvasScale || 1;

    const scaleStyle: React.CSSProperties | undefined =
        sizeCompensation !== 1 ? ({'--answer-comment-scale': sizeCompensation} as React.CSSProperties) : undefined;

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const {root, isScaledContainer: scaled, inFrame: framed} = getPortalTarget(anchorRef.current);
        setPortalRoot(root);
        setIsScaledContainer(scaled);
        setInFrame(framed);
    }, [anchorRef]);

    useEffect(() => {
        if (isMobile || !showComment || !anchorRef.current || !portalRoot) {
            return (): void => undefined;
        }

        const updateDropdownPosition = (): void => {
            const anchor = anchorRef.current;
            if (!anchor) return;

            const anchorRect = anchor.getBoundingClientRect();
            const portalRect = portalRoot.getBoundingClientRect();

            const dropdownHeight = dropdownRef.current?.scrollHeight ?? 0;
            const {shouldOpenTop, maxHeight} = calcDropdownPlacement(anchorRect, portalRect, dropdownHeight, effectiveScale);

            setDropdownPlacement(shouldOpenTop ? 'top' : 'bottom');
            setDropdownStyle(buildDropdownStyle(anchorRect, portalRect, shouldOpenTop, maxHeight, effectiveScale));
        };

        updateDropdownPosition();
        requestAnimationFrame(updateDropdownPosition);

        const frameWindow = anchorRef.current.ownerDocument.defaultView;
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);
        frameWindow?.addEventListener('resize', updateDropdownPosition);
        frameWindow?.addEventListener('scroll', updateDropdownPosition, true);

        return (): void => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
            frameWindow?.removeEventListener('resize', updateDropdownPosition);
            frameWindow?.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [isMobile, showComment, portalRoot, anchorRef, effectiveScale]);

    useEffect(() => {
        if (isMobile || !showComment) {
            return (): void => undefined;
        }

        const handleOutsideInteraction = (event: MouseEvent | TouchEvent): void => {
            const path = event.composedPath();
            const isInsideAnchor = !!anchorRef.current && path.includes(anchorRef.current);
            const isInsideDropdown = !!dropdownRef.current && path.includes(dropdownRef.current);

            if (!isInsideAnchor && !isInsideDropdown) {
                setShowComment?.(false);
            }
        };

        const documents = new Set<Document>([document]);
        const ownerDocument = anchorRef.current?.ownerDocument;
        if (ownerDocument) documents.add(ownerDocument);

        documents.forEach((eventDocument) => {
            eventDocument.addEventListener('mousedown', handleOutsideInteraction);
            eventDocument.addEventListener('touchstart', handleOutsideInteraction);
        });

        return () => {
            documents.forEach((eventDocument) => {
                eventDocument.removeEventListener('mousedown', handleOutsideInteraction);
                eventDocument.removeEventListener('touchstart', handleOutsideInteraction);
            });
        };
    }, [isMobile, showComment, anchorRef, setShowComment]);

    if (isMobile) {
        return (
            <>
                <div
                    className="c-questionElement__answer_comment"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowComment?.(true);
                    }}
                >
                    <Icon icon="solar:chat-line-bold" />
                </div>
                <BottomSheet open={showComment} onDismiss={() => setShowComment?.(false)} title={answer ?? ''} content={comment} />
            </>
        );
    }

    return (
        <>
            <div
                className="c-questionElement__answer_comment"
                style={scaleStyle}
                onClick={() => {
                    setShowComment?.(!showComment);
                    setHoverOpened(false);
                }}
                onMouseEnter={() => {
                    if (!showComment) {
                        setShowComment?.(true);
                        setHoverOpened(true);
                    }
                }}
                onMouseLeave={() => {
                    if (hoverOpened) {
                        setShowComment?.(false);
                        setHoverOpened(false);
                    }
                }}
            >
                <Icon icon="solar:chat-line-bold" />
            </div>
            {portalRoot &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={`c-questionElement__answer_dropdown ${showComment ? 'is-visible' : ''} ${
                            dropdownPlacement === 'top' ? 'is-top' : ''
                        }`}
                        style={{...dropdownStyle, ...scaleStyle}}
                    >
                        {comment}
                    </div>,
                    portalRoot
                )}
        </>
    );
}

export default AnswerComment;
